import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import Icon from '../../Icon';

function AdminEmailView({ user }) {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [notification, setNotification] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const res = await api.get('/auth/users');
        const allUsers = Array.isArray(res?.data?.users) ? res.data.users : [];
        const tenantUsers = allUsers.filter(u => {
          const hasGlobalTenantRole = u.role === 'T';
          const hasPropertyTenantRole = Array.isArray(u.propertyRoles) && u.propertyRoles.some(pr => pr.role === 'T');
          return hasGlobalTenantRole || hasPropertyTenantRole;
        });
        setTenants(tenantUsers);
        setFilteredTenants(tenantUsers);
      } catch (e) {
        console.error('Failed to fetch tenants', e);
        showNotification('error', 'Failed to load tenants');
      }
    };
    fetchTenants();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTenants(tenants);
      return;
    }
    const q = searchQuery.toLowerCase();
    const filtered = tenants.filter(t => 
      (t.name || '').toLowerCase().includes(q) ||
      (t.email || '').toLowerCase().includes(q) ||
      (t.phone || '').toLowerCase().includes(q) ||
      (t.idNumber || '').toLowerCase().includes(q)
    );
    setFilteredTenants(filtered);
  }, [searchQuery, tenants]);

  const handleSelectTenant = (tenant) => {
    setSelectedTenant(tenant);
    setEmailAddress(tenant.email || '');
    setDropdownOpen(false);
    setSearchQuery('');
  };

  const showNotification = (type, msg) => {
    setNotification({ type, message: msg });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSendEmail = async () => {
    if (!selectedTenant) {
      showNotification('error', 'Please select a tenant');
      return;
    }
    if (!emailAddress.trim()) {
      showNotification('error', 'Email address is required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailAddress)) {
      showNotification('error', 'Please enter a valid email address');
      return;
    }
    if (!subject.trim()) {
      showNotification('error', 'Subject is required');
      return;
    }
    if (!message.trim()) {
      showNotification('error', 'Message cannot be empty');
      return;
    }

    try {
      setSending(true);
      const response = await api.post('/frp/admin/send-email', {
        to: emailAddress,
        subject: subject,
        message: message,
        tenantId: selectedTenant._id
      });
      
      const successMsg = response.data?.dryRun 
        ? `Email would be sent to ${selectedTenant.name} (dry-run mode)`
        : `Email sent successfully to ${selectedTenant.name}`;
      
      showNotification('success', successMsg);
      setMessage('');
      setSubject('');
      setSelectedTenant(null);
      setEmailAddress('');
    } catch (e) {
      console.error('Failed to send email', e);
      showNotification('error', e?.response?.data?.error || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'T';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <div className="min-h-screen font-sans pb-8 relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
    }}>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-3xl" />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-6 right-6 z-50" style={{ animation: 'slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)' }}>
          <div className={`flex items-center gap-4 px-5 py-4 rounded-2xl shadow-2xl backdrop-blur-xl border ${
            notification.type === 'success' 
              ? 'bg-emerald-500/90 border-emerald-400/30 text-white' 
              : 'bg-rose-500/90 border-rose-400/30 text-white'
          }`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              notification.type === 'success' ? 'bg-white/20' : 'bg-white/20'
            }`}>
              {notification.type === 'success' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <p className="text-sm font-medium pr-2">{notification.message}</p>
            <button
              onClick={() => setNotification(null)}
              className="ml-auto w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 pt-6 pb-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="group flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-white/70 hover:text-white"
            >
              <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Dashboard</span>
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin/email-history')}
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-400/30 hover:border-cyan-300/60 text-cyan-100 hover:text-white text-sm font-medium transition-all shadow-sm shadow-cyan-500/20"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v4H4zM4 12h10v8H4z" />
                </svg>
                <span>History</span>
              </button>
              <button
                onClick={() => navigate('/admin/email-history')}
                className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-400/30 text-cyan-100 hover:text-white transition-all shadow-sm shadow-cyan-500/20"
                title="Email History"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v4H4zM4 12h10v8H4z" />
                </svg>
              </button>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20">
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                <span className="text-xs font-medium text-cyan-300">Connected</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-gradient-to-br from-cyan-500 to-emerald-500 shadow-2xl shadow-cyan-500/25 mb-6 relative">
            <svg className="w-10 h-10 sm:w-12 sm:h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-400 rounded-full flex items-center justify-center shadow-lg">
              <svg className="w-3.5 h-3.5 text-emerald-900" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Compose Email
          </h1>
          <p className="text-slate-400 text-base sm:text-lg max-w-xl mx-auto">
            Send personalized messages directly to your tenants with ease
          </p>
        </div>
      </div>

      {/* Main Card */}
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl overflow-hidden" style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
          backdropFilter: 'blur(40px)',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 0 rgba(255,255,255,0.1)'
        }}>
          {/* Card Header Accent */}
          <div className="h-1 bg-gradient-to-r from-cyan-500 via-emerald-500 to-cyan-500" />
          
          <div className="p-6 sm:p-8 lg:p-10 space-y-8">
            {/* Tenant Selection */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-white/90">
                <span className="w-6 h-6 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-xs">1</span>
                Select Recipient
              </label>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl transition-all text-left ${
                    dropdownOpen 
                      ? 'bg-white/10 border-cyan-500/50 ring-2 ring-cyan-500/20' 
                      : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20'
                  } border`}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {selectedTenant ? (
                      <>
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center text-white font-bold shadow-lg shadow-cyan-500/20 flex-shrink-0">
                          {getInitials(selectedTenant.name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-base font-semibold text-white truncate">{selectedTenant.name}</p>
                          <p className="text-sm text-slate-400 truncate">{selectedTenant.email}</p>
                        </div>
                        <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                          <span className="text-xs font-medium text-emerald-400">Selected</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-12 h-12 rounded-2xl bg-white/5 border border-dashed border-white/20 flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <span className="text-slate-400">Choose a tenant to email...</span>
                      </>
                    )}
                  </div>
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all ${dropdownOpen ? 'bg-cyan-500/20 rotate-180' : 'bg-white/5'}`}>
                    <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* Dropdown */}
                {dropdownOpen && (
                  <div className="absolute z-50 w-full mt-3 rounded-2xl overflow-hidden" style={{
                    background: 'linear-gradient(180deg, rgba(30,41,59,0.98) 0%, rgba(15,23,42,0.98) 100%)',
                    backdropFilter: 'blur(40px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)'
                  }}>
                    <div className="p-4 border-b border-white/10">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          placeholder="Search by name, email, or phone..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-11 pr-4 py-3 text-sm bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 text-white placeholder-slate-500 transition-all"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="max-h-80 overflow-y-auto">
                      {filteredTenants.length === 0 ? (
                        <div className="p-8 text-center">
                          <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center mx-auto mb-3">
                            <svg className="w-8 h-8 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>
                          <p className="text-sm text-slate-500">No tenants found</p>
                        </div>
                      ) : (
                        filteredTenants.map((tenant, idx) => (
                          <button
                            key={tenant._id}
                            onClick={() => handleSelectTenant(tenant)}
                            className="w-full flex items-center gap-4 px-5 py-4 hover:bg-white/5 transition-all text-left group"
                            style={{ animation: `fadeIn 0.3s ease-out ${idx * 0.05}s both` }}
                          >
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 group-hover:from-cyan-500 group-hover:to-emerald-500 transition-all shadow-lg">
                              {getInitials(tenant.name)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-white truncate group-hover:text-cyan-300 transition-colors">{tenant.name}</p>
                              {tenant.email && (
                                <p className="text-xs text-slate-400 truncate mt-0.5">{tenant.email}</p>
                              )}
                            </div>
                            <svg className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 transition-colors opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              {selectedTenant && !selectedTenant.email && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <svg className="w-4 h-4 text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-sm text-amber-300">This tenant has no email address on record</span>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            </div>

            {/* Email Address */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-white/90">
                <span className="w-6 h-6 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-xs">2</span>
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
                <input
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="tenant@example.com"
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 text-white placeholder-slate-500 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={!selectedTenant}
                />
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-white/90">
                <span className="w-6 h-6 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-xs">3</span>
                Subject Line
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter a compelling subject..."
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 text-white placeholder-slate-500 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={!selectedTenant}
                />
              </div>
            </div>

            {/* Message */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-white/90">
                <span className="w-6 h-6 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-xs">4</span>
                Message Body
              </label>
              <div className="relative">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write your message here. Be clear and concise..."
                  rows={8}
                  className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 text-white placeholder-slate-500 transition-all disabled:opacity-40 disabled:cursor-not-allowed resize-none"
                  disabled={!selectedTenant}
                  style={{ lineHeight: '1.7' }}
                />
                <div className="absolute bottom-3 right-3 flex items-center gap-2">
                  <span className={`text-xs font-mono px-2 py-1 rounded-lg ${
                    message.length > 1000 ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-slate-500'
                  }`}>
                    {message.length.toLocaleString()} chars
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={handleSendEmail}
                disabled={!selectedTenant || !emailAddress.trim() || !subject.trim() || !message.trim() || sending}
                className="flex-1 relative group overflow-hidden rounded-2xl disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-emerald-500 transition-transform group-hover:scale-105 group-disabled:scale-100" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{
                  background: 'linear-gradient(45deg, transparent 25%, rgba(255,255,255,0.1) 50%, transparent 75%)',
                  backgroundSize: '200% 200%',
                  animation: 'shimmer 2s infinite'
                }} />
                <div className="relative flex items-center justify-center gap-3 px-8 py-4 text-white font-semibold">
                  {sending ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      <span>Send Email</span>
                    </>
                  )}
                </div>
              </button>
              <button
                onClick={() => {
                  setSelectedTenant(null);
                  setEmailAddress('');
                  setSubject('');
                  setMessage('');
                }}
                disabled={sending}
                className="px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-slate-300 hover:text-white rounded-2xl transition-all font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Clear Form
              </button>
            </div>
          </div>
        </div>

        {/* Bottom decorative element */}
        <div className="flex justify-center mt-8">
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>Emails are sent securely via encrypted connection</span>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        
        /* Custom scrollbar */
        .max-h-80::-webkit-scrollbar {
          width: 6px;
        }
        .max-h-80::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.05);
          border-radius: 3px;
        }
        .max-h-80::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 3px;
        }
        .max-h-80::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.2);
        }
      `}</style>
    </div>
  );
}

export default AdminEmailView;
